"use client";

import { RiPlayListLine } from "@remixicon/react";
import type { DbSetReview, ProcessedSet, Review } from "@workspace/types";
import { Card } from "@workspace/ui/components/card";
import { memo, Suspense, useCallback } from "react";
import { RealtimeSetCard } from "@/components/shared/realtime-set-card";
import type { TabVariant } from "./use-tab-variant";

interface StagesTabProps {
	// Core data
	stagesData: { stageName: string; sets: ProcessedSet[] }[];
	unfilteredStagesData?: { stageName: string; sets: ProcessedSet[] }[];
	dbStagesList: Array<{ id: string; name: string; position?: number }>;
	festivalSlug?: string;

	// Variant configuration
	variant: TabVariant;

	// Authentication/ownership
	authState: boolean; // isOwnProfile for personal, isAuthenticated for public

	// Reviews data - supports both formats
	setReviews?: DbSetReview[]; // Personal view format
	reviews?: Review[]; // Public view simplified format

	// Schedule data
	savedSchedule: string[];

	// Display state
	selectedDay: string;
	selectedStage: string;

	// Public view specific
	currentShowOnlySaved?: boolean;
	onClearFilters?: () => void;

	// Event handlers
	onStartReview: (setId: string) => void;
	onToggleSetInSchedule: (setId: string) => void;
	onStageChange?: (stage: string) => void;
	onAuthRequired?: () => void;
}

export const StagesTab = memo(function StagesTab({
	stagesData,
	unfilteredStagesData,
	dbStagesList,
	festivalSlug,
	variant,
	authState,
	setReviews,
	reviews,
	savedSchedule,
	selectedDay,
	selectedStage = "all",
	currentShowOnlySaved = false,
	onClearFilters,
	onStartReview,
	onToggleSetInSchedule,
	onStageChange,
	onAuthRequired,
}: StagesTabProps) {
	// Always use RealtimeSetCard for real-time updates
	const SetCardComponent = RealtimeSetCard;

	// Convert reviews to the format expected by SetCard
	// Returns null (not undefined) when reviews are batched but no review exists for this set
	// This prevents the per-set realtime hook from re-enabling and fetching stale DB data
	const getReviewForCard = useCallback(
		(setId: string): Review | null | undefined => {
			if (variant === "personal") {
				// For personal view, reviews are already in camelCase format from UsernameFestivalPageClient
				if (reviews) {
					return reviews.find((r) => r.setId === setId) ?? null;
				}
				// Fallback to setReviews if reviews not provided (shouldn't happen)
				if (setReviews) {
					const review = setReviews.find(
						(r) => r.set_id === setId || r.program_set_id === setId,
					);
					return review
						? {
								setId: review.set_id || review.program_set_id || setId,
								artistId: review.artist_id,
								rating: review.rating,
								content: review.content || "",
								loved: review.loved,
								sawLive: review.saw_live,
								sawStream: review.saw_stream,
								wantToSee: review.want_to_see,
								mustSee: review.must_see,
							}
						: null;
				}
			}
			if (variant === "public" && reviews) {
				return reviews.find((r) => r.setId === setId) ?? null;
			}
			return undefined;
		},
		[variant, setReviews, reviews],
	);

	const readOnly = !authState;

	// Check if empty state is due to filters
	const isEmptyDueToFilters = !!(
		unfilteredStagesData &&
		unfilteredStagesData.length > 0 &&
		stagesData.length === 0
	);

	// Empty state rendering
	if (stagesData.length === 0) {
		return (
			<Card className="p-8 text-center">
				<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
					<RiPlayListLine className="h-6 w-6 text-muted-foreground" />
				</div>
				<h3 className="mb-1 font-semibold text-xl">
					{isEmptyDueToFilters || (variant === "public" && currentShowOnlySaved)
						? authState
							? "No Sets Match Your Filters"
							: "Sign In Required"
						: "No Sets Found"}
				</h3>
				<p className="mb-4 text-muted-foreground text-sm">
					{variant === "public" && currentShowOnlySaved && !authState
						? "Sign in to save sets and filter your schedule."
						: isEmptyDueToFilters ||
								(variant === "public" && currentShowOnlySaved)
							? `No sets match your filters for ${selectedStage === "all" ? "any stage" : dbStagesList.find((s) => s.id === selectedStage)?.name || "this stage"} on ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}.`
							: selectedStage === "all"
								? `No sets scheduled for ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}.`
								: `No sets found for ${dbStagesList.find((s) => s.id === selectedStage)?.name || "this stage"} on ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}.`}
				</p>
				{((variant === "public" && currentShowOnlySaved) ||
					selectedStage !== "all") && (
					<div className="flex justify-center gap-2">
						{variant === "public" && currentShowOnlySaved && onClearFilters && (
							<button
								type="button"
								onClick={onClearFilters}
								className="rounded-lg bg-muted px-3 py-2 text-sm transition-colors hover:bg-muted/80"
							>
								Show All Sets
							</button>
						)}
						{selectedStage !== "all" && onStageChange && (
							<button
								type="button"
								onClick={() => onStageChange("all")}
								className="rounded-lg bg-muted px-3 py-2 text-sm transition-colors hover:bg-muted/80"
							>
								Show All Stages
							</button>
						)}
					</div>
				)}
			</Card>
		);
	}

	// Stages content rendering
	return (
		<div className="grid gap-4">
			{stagesData.map(({ stageName, sets }) => {
				// Check if this stage has sets in unfiltered data
				const unfilteredStage = unfilteredStagesData?.find(
					(s) => s.stageName === stageName,
				);
				const isStageEmptyDueToFilters = !!(
					unfilteredStage &&
					unfilteredStage.sets.length > 0 &&
					sets.length === 0
				);

				return (
					<div key={stageName} className="space-y-2.5 min-w-0">
						<h3 className="font-semibold">{stageName}</h3>
						{sets.length === 0 ? (
							<Card className="p-4">
								<p className="text-muted-foreground text-sm">
									{isStageEmptyDueToFilters ||
									(variant === "public" && currentShowOnlySaved)
										? `No sets match your filters for ${stageName} on ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}.`
										: `No sets found for ${stageName} on ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}.`}
								</p>
							</Card>
						) : (
							<div className="space-y-2">
								{sets.map((set: ProcessedSet) => {
									const review = getReviewForCard(set.id);
									return (
										<Suspense
											key={set.id}
											fallback={
												<div className="h-20 animate-pulse rounded-lg bg-muted" />
											}
										>
											<SetCardComponent
												set={set}
												review={review}
												savedSchedule={savedSchedule}
												festivalSlug={festivalSlug}
												variant={variant}
												onStartReview={onStartReview}
												onToggleSetInSchedule={onToggleSetInSchedule}
												readOnly={readOnly}
												onAuthRequired={onAuthRequired}
												layout="horizontal"
												hideDayLabel
											/>
										</Suspense>
									);
								})}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
});
